import { blue, green, grey, red } from '@ant-design/colors'
import { EstimateStatuses, ProjectStatuses, ProjectType, ProjectTypes } from '../../common/constants'

export type KanbanColumn = {
  status: number
  label: string
  accent: string
  acceptsDrops: boolean
  closedReason?: string
}

/* Every job status is a stored value the API can set: Projects.Status for projects,
   and a rollup of ProjectWorkareas.Status for tickets and maintenance. Scheduling a
   visit moves an Unscheduled job to Scheduled, but nothing moves it back when visits
   are deleted, so the status is not a mirror of the calendar and the board is free
   to set it. */
export const jobColumns: KanbanColumn[] = [
  { status: ProjectStatuses.Unscheduled, label: 'Unscheduled', accent: grey.primary as string, acceptsDrops: true },
  { status: ProjectStatuses.Scheduled, label: 'Scheduled', accent: blue.primary as string, acceptsDrops: true },
  { status: ProjectStatuses.WaitingOnParts, label: 'Waiting on Parts', accent: red.primary as string, acceptsDrops: true },
  { status: ProjectStatuses.Completed, label: 'Completed', accent: green.primary as string, acceptsDrops: true }
]

/* Estimate status, unlike job status, is not stored at all — GetEstimateStatus derives
   it from the proposals: approved proposal, then any recipient, else a draft. Only Lost
   is a real column on Projects, so it is the only estimate column a drop can set. */
export const estimateColumns: KanbanColumn[] = [
  { status: EstimateStatuses.Unsent, label: 'Drafts', accent: grey.primary as string, acceptsDrops: false, closedReason: 'Set by sending a proposal' },
  { status: EstimateStatuses.Sent, label: 'Sent', accent: blue.primary as string, acceptsDrops: false, closedReason: 'Set by sending a proposal' },
  { status: EstimateStatuses.Approved, label: 'Approved', accent: green.primary as string, acceptsDrops: false, closedReason: 'Set when a customer approves' },
  { status: EstimateStatuses.Lost, label: 'Lost', accent: red.primary as string, acceptsDrops: true }
]

export const columnsFor = (sold: boolean): KanbanColumn[] => sold ? jobColumns : estimateColumns

export const DRAG_PREVIEW_NOTICE = 'Preview only — the move is not saved yet.'

/* Only a sold project has a status of its own to write. A ticket or maintenance job
   takes its status from its work areas, so a drop there needs the work-area picker,
   and an estimate's comes from its proposals. Those stay previews until each has a
   write of its own. */
export const statusIsSavableFor = (
  { sold, deleted, jobType }: { sold: boolean, deleted?: boolean, jobType?: ProjectType }
) => sold && !deleted && jobType === ProjectTypes.Project

/* A ticket or maintenance job has no status of its own — it rolls up from its work areas —
   so a drop has to ask which ones it applies to before anything can be written. */
export const needsWorkareaPickerFor = (
  { sold, deleted, jobType }: { sold: boolean, deleted?: boolean, jobType?: ProjectType }
) => sold && !deleted && (jobType === ProjectTypes.Ticket || jobType === ProjectTypes.Maintenance)

export const acceptsDrops = (columns: KanbanColumn[], status: number) =>
  columns.find(x => x.status === status)?.acceptsDrops ?? false

/* Lost is the only estimate status that is stored, so it is the only one a drop can set —
   but a card already in Lost has to be able to come back out, or one mistaken drop is
   permanent. Dropping it anywhere else clears Lost and lets the proposals decide where it
   lands, which may not be the column it was dropped on. */
export const isUnLosing = (sold: boolean, from: number, to: number) =>
  !sold && from === EstimateStatuses.Lost && to !== EstimateStatuses.Lost

export const labelFor = (columns: KanbanColumn[], status: number) =>
  columns.find(x => x.status === status)?.label ?? ''

export const closedReasonFor = (columns: KanbanColumn[], status: number) =>
  columns.find(x => x.status === status)?.closedReason

export const initialsOf = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return ''
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const avatarPalette = ['#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#13c2c2']

export const avatarColourFor = (name: string) => {
  const seed = [...name].reduce((total, char) => total + char.charCodeAt(0), 0)
  return avatarPalette[seed % avatarPalette.length]
}

export const compactDollars = (cents: number) => {
  const dollars = Math.round(cents / 100)
  if (Math.abs(dollars) >= 1_000_000) {
    return `$${(dollars / 1_000_000).toFixed(dollars % 1_000_000 === 0 ? 0 : 1)}M`
  }
  return `$${dollars.toLocaleString('en-US')}`
}
